import { useState } from "react";
import { Outlet, NavLink, Link, useLocation, useSearchParams } from "react-router-dom";
import { cn, navLinkClass } from "@/lib/utils";
import { Button } from "@satyrsmc/shared/components";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { EVENT_TYPE_LABELS, EVENT_TYPES } from "@/lib/event-constants";
import { useAuth } from "@/state/AuthContext";
import {
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  Calendar,
  Bike,
  AlertTriangle,
  DollarSign,
} from "lucide-react";

const typeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  badger: Calendar,
  social: Calendar,
  pioneer_run: Bike,
  rides: Bike,
};

export function EventsLayout() {
  const { isAdmin } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // The events table lives at the index route; type selection is a query
  // param there, so NavLink's path-only `isActive` can't tell it apart —
  // compute active state from the current `type` param instead.
  const onEventsIndex = location.pathname === "/admin/events";
  const currentType = searchParams.get("type");

  return (
    <div className="flex min-h-0 flex-1 gap-6">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden md:block sticky top-16 self-start shrink-0 border-r pr-4 transition-[width] duration-200 ease-in-out",
          collapsed ? "w-14" : "w-56",
        )}
      >
        <div
          className={cn("flex items-center py-4", collapsed ? "justify-center" : "justify-between")}
        >
          {!collapsed && (
            <span className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Events
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </Button>
        </div>
        <nav className="space-y-6 py-4" aria-label="Events section">
          <div className="space-y-0.5">
            <Link
              to="/admin/events"
              className={navLinkClass({ isActive: onEventsIndex && !currentType }, collapsed)}
              title={collapsed ? "All events" : undefined}
            >
              <Calendar className="size-4 shrink-0" />
              {!collapsed && <span>All</span>}
            </Link>
            <div className={cn("space-y-0.5", !collapsed && "pl-4")}>
              {EVENT_TYPES.map((type) => {
                const Icon = typeIcons[type] ?? Calendar;
                return (
                  <Link
                    key={type}
                    to={`/admin/events?type=${type}`}
                    className={navLinkClass(
                      { isActive: onEventsIndex && currentType === type },
                      collapsed,
                    )}
                    title={collapsed ? EVENT_TYPE_LABELS[type] : undefined}
                  >
                    <Icon className="size-4 shrink-0" />
                    {!collapsed && <span>{EVENT_TYPE_LABELS[type]}</span>}
                  </Link>
                );
              })}
            </div>
            <NavLink
              to="/admin/events/incidents"
              className={({ isActive }) => navLinkClass({ isActive }, collapsed)}
              title={collapsed ? "Incidents" : undefined}
            >
              <AlertTriangle className="size-4 shrink-0" />
              {!collapsed && <span>Incidents</span>}
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/admin/budgeting/projections"
                className={({ isActive }) => navLinkClass({ isActive }, collapsed)}
                title={collapsed ? "Budgeting" : undefined}
              >
                <DollarSign className="size-4 shrink-0" />
                {!collapsed && <span>Budgeting</span>}
              </NavLink>
            )}
          </div>
        </nav>
      </aside>

      <div className="min-w-0 flex-1 flex flex-col">
        {/* Mobile menu button */}
        <div className="md:hidden mb-4">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" aria-label="Open Events menu">
                <Menu className="size-4" />
                Events
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 max-h-full overflow-y-auto">
              <div className="p-4 pt-14 space-y-6">
                <span className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Events
                </span>
                <nav className="space-y-6" aria-label="Events section">
                  <div className="space-y-0.5">
                    <Link
                      to="/admin/events"
                      className={navLinkClass({ isActive: onEventsIndex && !currentType }, false)}
                      onClick={() => setMobileNavOpen(false)}
                    >
                      <Calendar className="size-4 shrink-0" />
                      <span>All</span>
                    </Link>
                    <div className="space-y-0.5 pl-4">
                      {EVENT_TYPES.map((type) => {
                        const Icon = typeIcons[type] ?? Calendar;
                        return (
                          <Link
                            key={type}
                            to={`/admin/events?type=${type}`}
                            className={navLinkClass(
                              { isActive: onEventsIndex && currentType === type },
                              false,
                            )}
                            onClick={() => setMobileNavOpen(false)}
                          >
                            <Icon className="size-4 shrink-0" />
                            <span>{EVENT_TYPE_LABELS[type]}</span>
                          </Link>
                        );
                      })}
                    </div>
                    <NavLink
                      to="/admin/events/incidents"
                      className={({ isActive }) => navLinkClass({ isActive }, false)}
                      onClick={() => setMobileNavOpen(false)}
                    >
                      <AlertTriangle className="size-4 shrink-0" />
                      <span>Incidents</span>
                    </NavLink>
                    {isAdmin && (
                      <NavLink
                        to="/admin/budgeting/projections"
                        className={({ isActive }) => navLinkClass({ isActive }, false)}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        <DollarSign className="size-4 shrink-0" />
                        <span>Budgeting</span>
                      </NavLink>
                    )}
                  </div>
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <Outlet />
      </div>
    </div>
  );
}
